import { useRef } from 'react';
import Markdown from 'markdown-to-jsx';
import { useWindowSize } from 'react-use';
import dynamic from 'next/dynamic';

import CodeBlock from '@/components/renderers/CodeBlock';
import Image from '@/components/renderers/Image';
import IFrame from '@/components/renderers/IFrame';
import InfoBlock from '@/components/renderers/InfoBlock';
import DangerBlock from '@/components/renderers/DangerBlock';
import SuccessBlock from '@/components/renderers/SuccessBlock';
import WarningBlock from '@/components/renderers/WarningBlock';
import { Heading } from '@/components/docs/MDX';
import { Label } from '@/components/blog/table-of-contents';
import AsideCtaCards from '@/components/blog/aside-cta-cards';

type ContentProps = {
  content: string;
  postTitle: string;
};

const DynamicTableOfContents = dynamic(() => import(`./table-of-contents`), {
  ssr: false,
});

const DynamicShareSheetWithNoSSR = dynamic(() => import(`../ShareSheet`), {
  ssr: false,
});

export default function Content({ content, postTitle }: ContentProps) {
  const contentRef = useRef<HTMLElement>(null);

  const { width } = useWindowSize();
  const isMobile = width <= 1024;

  const headingRegex = /^(#{2,2} .*[\n\r\v\t\s]*)$/gim;

  const contentHeading = content.match(headingRegex);
  const links = contentHeading?.map((link) => {
    const level = link.match(/^#{2}/);

    return {
      title: link.slice(3),
      depth: level ? level[0].length : 0,
    };
  });

  return (
    <section className="grid gap-x-20 grid-cols-1 lg:grid-cols-12 relative">
      {!isMobile && (
        <aside className="w-full col-span-3 hidden lg:block h-full">
          <div className="sticky top-30 space-y-10">
            {links && (
              <DynamicTableOfContents links={links} contentRef={contentRef} />
            )}

            <div>
              <Label>Share</Label>

              <DynamicShareSheetWithNoSSR
                title={postTitle}
                className="border-pigeon-post border-l py-2 px-4"
              />
            </div>

            <AsideCtaCards />
          </div>
        </aside>
      )}
      <main className="lg:col-span-9 justify-end" ref={contentRef}>
        <Markdown
          className="prose lg:prose-lg prose-blog max-w-none w-full"
          options={{
            forceBlock: true,
            overrides: {
              code: (props) => <CodeBlock {...props} noCopy />,
              pre: ({ children }) => <>{children}</>,
              h1: (props) => <Heading {...props} level="1" as="blog" />,
              h2: (props) => <Heading {...props} level="2" as="blog" />,
              h3: (props) => <Heading {...props} level="3" as="blog" />,
              h4: (props) => <Heading {...props} level="4" as="blog" />,
              iframe: IFrame,
              img: Image,
              InfoBlock,
              DangerBlock,
              SuccessBlock,
              WarningBlock,
            },
          }}
        >
          {content}
        </Markdown>
      </main>
    </section>
  );
}
